import { Button } from '@tovala/component-library'
import {
  ComponentProps,
  forwardRef,
  Fragment,
  ReactNode,
  useState,
} from 'react'
import { Link, Link as ReactRouterLink, useNavigate } from 'react-router-dom'

import { events } from 'analytics/events'
import { track } from 'utils/analytics'

import { useUser } from 'contexts/user'
import Collapsible, {
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/common/Collapsible'
import BirthdayForm from './components/BirthdayForm'
import DeleteAccountModal from './components/DeleteAccountModal'
import ProfileHeading from '../AccountHeading'
import UpdateEmailForm from './components/UpdateEmailForm'
import UpdatePasswordForm from './components/UpdatePasswordForm'
import { queries } from '@tovala/browser-apis-combinedapi'
import { getActivePromoCount } from 'utils/incentives'
import { useQuery } from '@tanstack/react-query'
import { formatBirthday } from './utils'

const ProfilePage = () => {
  const navigate = useNavigate()
  const { user } = useUser()

  const { data: activeIncentives = [] } = useQuery({
    ...queries.users.activeIncentives({ userID: user.id }),
    enabled: user.subscription.status === 'active',
  })

  const { data: profile } = useQuery({
    ...queries.profile.detail({ userID: user.id }),
  })

  const activePromoCount = getActivePromoCount({
    activeIncentives,
    isFreeDessertCustomer: user.isFreeDessertCustomer,
  })

  const [dialogType, setDialogType] = useState<'delete' | null>(null)

  return (
    <>
      {dialogType === 'delete' && (
        <DeleteAccountModal
          onClickClose={() => {
            track(events.TAPS_DELETE_ACCOUNT_CANCEL)
            setDialogType(null)
          }}
          user={user}
        />
      )}
      <div className="hidden md:block">
        <ProfileHeading to="/account">Profile</ProfileHeading>
      </div>
      <div className="w-full max-w-account space-y-4 md:max-w-full md:p-4">
        <h1 className="mb-6 text-k/44_110 md:hidden">Profile</h1>

        <ProfileCard>
          <ProfileCollapsible
            additionalButton={
              <ProfileButton
                onClick={() => {
                  navigate('/logout')
                }}
              >
                Logout
              </ProfileButton>
            }
            title={
              <>
                <div className="truncate">{user.info.email}</div>
                <div className="text-k/14_120">{user.info.name}</div>
              </>
            }
            triggerOpenText="Edit Account"
          >
            {({ close }) => {
              return <UpdateEmailForm closeCollapsible={close} user={user} />
            }}
          </ProfileCollapsible>
        </ProfileCard>

        <ProfileCard>
          <ProfileCollapsible title="Password" triggerOpenText="Change">
            {({ close }) => {
              return (
                <>
                  <div className="mb-6 text-k/14_120 underline">
                    <ReactRouterLink to="/forgot-password">
                      Forgot password or need to set one?
                    </ReactRouterLink>
                  </div>
                  <UpdatePasswordForm closeCollapsible={close} user={user} />
                </>
              )
            }}
          </ProfileCollapsible>
        </ProfileCard>

        <ProfileCard>
          <ProfileCollapsible
            onTriggerClick={(currentOpen) => {
              if (!currentOpen) {
                track(events.OPENS_ACCOUNT_BIRTHDAY)
              }
            }}
            title={
              <>
                <div>Birthday</div>
                {profile && (
                  <div className="text-k/14_120">
                    {profile.birthday
                      ? formatBirthday(profile.birthday)
                      : 'Add your birthday'}
                  </div>
                )}
              </>
            }
            triggerOpenText="Edit"
          >
            {({ close }) => {
              if (!profile) {
                return <div className="text-k/14_120 text-grey-9">Loading…</div>
              }

              return (
                <BirthdayForm
                  birthday={profile.birthday}
                  closeCollapsible={close}
                  userID={user.id}
                />
              )
            }}
          </ProfileCollapsible>
        </ProfileCard>

        {!user.subscription.isCommitment && (
          <ProfileCard>
            <ProfileCardContent
              buttonText="Delete"
              description="Permanently deletes all saved settings, favorites, credits and
            your recipes."
              onButtonClick={() => {
                track(events.TAPS_DELETE_ACCOUNT, {
                  count_active_promos: activePromoCount,
                })
                setDialogType('delete')
              }}
              title="Delete Account"
            />
          </ProfileCard>
        )}

        <div className="space-x-4 pt-4">
          <Link to="/legal/privacy-policy">Privacy Policy</Link>
          <span>•</span>
          <Link to="/legal/terms-of-service">Terms of Service</Link>
        </div>
      </div>
    </>
  )
}

export default ProfilePage

const ProfileCard = ({ children }: { children: ReactNode }) => {
  return (
    <div className="rounded-lg bg-grey-2">
      <div className="p-6 md:px-4">{children}</div>
    </div>
  )
}

const ProfileCardContent = ({
  buttonText,
  description,
  onButtonClick,
  title,
}: {
  buttonText: string
  description: string
  onButtonClick(): void
  title: string
}) => {
  return (
    <div className="flex justify-between space-x-4">
      <div>
        <span className="text-k/18_120">{title}</span>

        <p className="mt-1 text-body-sm text-grey-9">{description}</p>
      </div>

      <ProfileButton onClick={onButtonClick}>{buttonText}</ProfileButton>
    </div>
  )
}

const ProfileCollapsible = ({
  additionalButton,
  children,
  onTriggerClick,
  title,
  triggerOpenText,
}: {
  additionalButton?: ReactNode
  children: (opts: { close(): void }) => JSX.Element
  onTriggerClick?: (currentOpen: boolean) => void
  title: ReactNode | string
  triggerOpenText: string
}) => {
  return (
    <Collapsible>
      {({ open }: { open: boolean }) => {
        return (
          <>
            <div className="flex items-center justify-between gap-4 sm:flex-wrap sm:justify-end">
              <div className="flex w-px grow flex-col text-k/18_120 sm:w-auto">
                {title}
              </div>

              <div className="flex space-x-4">
                {additionalButton}

                <CollapsibleTrigger as={Fragment}>
                  <ProfileButton onClick={() => onTriggerClick?.(open)}>
                    {!open ? `${triggerOpenText}` : 'Cancel'}
                  </ProfileButton>
                </CollapsibleTrigger>
              </div>
            </div>
            <CollapsibleContent open={open}>
              {({ close }: { close: () => void }) => {
                return <div className="mt-6">{children({ close })}</div>
              }}
            </CollapsibleContent>
          </>
        )
      }}
    </Collapsible>
  )
}

const ProfileButton = forwardRef<
  HTMLButtonElement,
  Omit<ComponentProps<typeof Button>, 'buttonStyle' | 'size'>
>(function ProfileButton(props, ref) {
  return (
    <div className="h-7 w-28 shrink-0 text-k/12_120">
      <Button ref={ref} {...props} buttonStyle="stroke" size="fluid" />
    </div>
  )
})
